import { formatDisplayDate } from './dateDisplay';
import { normalizeId } from './ids';

/**
 * THE THREE-DAY NOTICE (G2; the 10b contract): a draft holding files is kept
 * 30 days after its last save, and its held files go with it. Three days
 * before, keepr sends ONE in-app notification through the inbox — never an
 * email, since that would copy scratch data into a mailbox:
 *
 *   kind: 'draft.files_expiring', data: { draftId, collectionId, keptUntil }
 *
 * Its words never repeat what was typed (a draft's title is text nobody
 * reviewed): "A draft in <collection> has files that will be deleted on
 * <date>. Save the item or open the draft to keep them." The server writes
 * them; this module says the same when a row arrives without them, and gives
 * the row its door to the draft.
 */

export const DRAFT_FILES_NOTICE_KIND = 'draft.files_expiring';
// ⚑ keepr-api's platform notices keep a kind only when it is a lower-case
// slug (utils/systemNotify.js: letters, digits and '-'); the dotted kind
// would be dropped there, so its slug spelling is read too.
const KINDS = new Set([DRAFT_FILES_NOTICE_KIND, 'draft-files-expiring']);

export const isDraftFilesNotice = notification => KINDS.has(notification?.kind);

const dataOf = notification => (notification?.data && typeof notification.data === 'object' ? notification.data : {});

/**
 * The draft's door: the Drafts page, which opens that draft's form when the
 * draft is still there (`?draft=<id>`), or lists the drafts when it is not.
 */
export function draftNoticePath(notification) {
	const id = normalizeId(dataOf(notification).draftId);
	return id ? `/drafts?draft=${encodeURIComponent(id)}` : '/drafts';
}

export const DRAFT_FILES_NOTICE_TITLE = 'Files in a draft will be deleted soon';

/** The notice's sentence, from its data and the collection's name when known. */
export function draftNoticeText(notification, { collectionName = '' } = {}) {
	const data = dataOf(notification);
	const where = collectionName ? `A draft in ${collectionName}` : 'One of your drafts';
	const when = data.keptUntil ? formatDisplayDate(data.keptUntil) : '';
	return `${where} has files that will be deleted${when ? ` on ${when}` : ' soon'}. Save the item or open the draft to keep them.`;
}

/** The collection the notice is about: its data's, else the row's. */
export const draftNoticeCollectionId = notification => (
	normalizeId(dataOf(notification).collectionId) || normalizeId(notification?.collection_id) || null
);
